import { inject, OnDestroy, Service } from '@angular/core';
import { Router } from '@angular/router';
import { ExtendedGetResult } from '@fingerprintjs/fingerprintjs-pro';
import { BehaviorSubject, Observable, of, share, Subject, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Api, ApiClient, ApiTypes, httpStatus } from '../../api';
import { CONSTANTS } from '../../common/constants/constants';
import { CONSTANTS_ERRORS } from '../../common/constants/errors/errors.constants';
import { CONSTANTS_ROUTING } from '../../common/constants/routing/routing.constants';
import { IAdvertisement, IMedia } from '../../common/interfaces/advertisement.interface';
import { FingerprintService } from '../../core/providers/fingerprint/fingerprint.service';
import { UtilsService } from '../../core/providers/util/utils.service';
import { AdvertisementService } from '../advertisement/advertisement.service';
import { CONTACT_GUEST_CONSTANTS } from '../contact-guest/contact-guest.constants';
import { DEMOGRAPHICS_CONSTANTS } from '../demographics/demographics.constants';
import { DemographicsService } from '../demographics/demographics.service';
import { IDemographicsDto } from '../demographics/interfaces/demographics-dto.interface';
import { IDemographicsVisitorFlags } from '../demographics/interfaces/demographics.interface';
import { ONE_MORE_QUESTION_CONSTANTS } from '../one-more-question/one-more-question.constants';
import { toOneMoreQuestion } from '../one-more-question/one-more-question.mapper';
import { OneMoreQuestionService } from '../one-more-question/one-more-question.service';
import { IWheelOfFortuneConfig } from '../wheel-of-fortune/interfaces/wheel-of-fortune.interface';
import { WHEEL_OF_FORTUNE_ROUTING_CONSTANTS } from '../wheel-of-fortune/wheel-of-fortune-routing.constants';
import { WheelOfFortuneService } from '../wheel-of-fortune/wheel-of-fortune.service';
import { ISurveyContactGuestDto } from './interfaces/survey-contact-guest-dto.interface';
import { ISurveyDTO } from './interfaces/survey-dto.interface';
import { ICampaignSurvey, ISurvey } from './interfaces/survey.interface';
import { SurveyStates } from './survey-states.enum';
import { venues_without_fraud_detection } from './venues-exception';

/** The wheel as it looks when the age gate denies this guest a spin. */
const SUPPRESSED_WHEEL: IWheelOfFortuneConfig = {
  isActive: false,
  prizeText: null,
  isDemographicsEnabled: false,
  ageLimit: null,
};

/** What survey submit leaves in localStorage so the same venue is not answered again too soon. */
interface StoredVenueVisit {
  uuid: string;
  dateTime: string;
}

function readJson(key: string): unknown {
  const stored = localStorage.getItem(key);

  return stored === null ? null : JSON.parse(stored);
}

function isStoredVenueVisit(value: unknown): value is StoredVenueVisit {
  return (
    typeof value === 'object' &&
    value !== null &&
    'uuid' in value &&
    typeof value.uuid === 'string' &&
    'dateTime' in value &&
    typeof value.dateTime === 'string'
  );
}

const SURVEY_NOT_LOADED_MESSAGE = 'The survey has not been loaded, so there is nothing to submit.';

@Service()
export class SurveyService implements OnDestroy {
  private api = inject(ApiClient);
  private router = inject(Router);
  private wheelOfFortuneService = inject(WheelOfFortuneService);
  private advertisementService = inject(AdvertisementService);
  private demographicsService = inject(DemographicsService);
  private utilsService = inject(UtilsService);
  private fingerprintService = inject(FingerprintService);
  private oneMoreQuestionService = inject(OneMoreQuestionService);

  private surveyState$ = new BehaviorSubject<SurveyStates | null>(null);
  private surveyId: string | null = null;
  private surveyDTO: ISurveyDTO | null = null;
  private survey: ISurvey | null = null;
  private campaignSurvey: ICampaignSurvey | null = null;
  private askPermissionToContact = false;
  private onDestroy$: Subject<void> = new Subject<void>();
  private askOneMoreQuestion = false;
  private visitorFlags: IDemographicsVisitorFlags = {
    shouldAskForGender: false,
    shouldAskForAgeGroup: false,
  };

  fetchSurvey$(id: string): Observable<ISurvey> {
    return this.api.get(Api.surveys.takeaway.s, { pathParams: { token: id } }).pipe(
      catchError((error: unknown) => {
        const status = httpStatus(error);

        if (status === CONSTANTS.ERRORS.HTTP_404) {
          this.setSurveyState(SurveyStates.notFound);
        }
        if (status === CONSTANTS_ERRORS.HTTP_403) {
          this.setSurveyState(SurveyStates.back);
        }

        return throwError(() => error);
      })
    );
  }

  fetchAd$(token: string, fingerprint: string): Observable<IAdvertisement> {
    return this.api.get(Api.fbi.takeaway.advertisement.view.survey, {
      pathParams: { token },
      query: { f: fingerprint },
    });
  }

  fetchCampaignAd$(venueUuid: string, fingerprint: string): Observable<IAdvertisement> {
    return this.api.get(Api.fbi.takeaway.advertisement.view.venue, {
      pathParams: { venueUuid },
      query: { f: fingerprint },
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  saveSurvey$(
    surveyDTO: Api.surveys.takeaway.PostBody
  ): Observable<ApiTypes.SubmitTakeawaySurveyResponseDto> {
    localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.SURVEY_TOKEN, surveyDTO.token);

    return this.api
      .post(Api.surveys.takeaway, {
        body: surveyDTO,
      })
      .pipe(
        catchError((error: unknown) => {
          const status = httpStatus(error);

          if (status === CONSTANTS_ERRORS.HTTP_410) {
            this.blockHappyWheel();
          } else if (status === CONSTANTS_ERRORS.HTTP_403) {
            this.setSurveyState(SurveyStates.back);
            void this.router.navigate([
              `${CONSTANTS_ROUTING.ROUTES.ADD.MAIN}/${CONSTANTS_ROUTING.ROUTES.ADD.BACK}`,
            ]);
          } else {
            this.setSurveyState(SurveyStates.error);
          }

          return throwError(() => error);
        })
      );
  }

  saveSurveyContactGuest$(surveyContactGuestDto: ISurveyContactGuestDto): Observable<unknown> {
    return this.api
      .put(Api.surveys.takeaway.contactPermission, {
        body: surveyContactGuestDto,
      })
      .pipe(
        catchError((error: unknown) => {
          this.setSurveyState(SurveyStates.error);

          return throwError(() => error);
        })
      );
  }

  getSurveyState$(): Observable<SurveyStates> {
    return this.surveyState$.pipe(filter((state): state is SurveyStates => state !== null));
  }

  setSurveyState(state: SurveyStates) {
    this.surveyState$.next(state);
    window.scrollTo(0, 0);
  }

  setSurveyId(id: string) {
    this.surveyId = id;
  }

  getSurveyId(): string | null {
    return this.surveyId;
  }

  setSurvey(survey: ISurvey) {
    this.survey = survey;
    this.wheelOfFortuneService.setWheelConfig(survey.wheel);

    if (this.surveyId) {
      this.wheelOfFortuneService.setSurveyToken(this.surveyId);
    }
  }

  getSurvey(): ISurvey | null {
    return this.survey;
  }

  getAskOneMoreQuestion(): boolean {
    return this.askOneMoreQuestion;
  }

  setAskOneMoreQuestion(value: boolean): void {
    this.askOneMoreQuestion = value;
  }

  getAskPermissionToContact() {
    return this.askPermissionToContact;
  }

  setSurveyDTO(survey: ISurveyDTO) {
    this.surveyDTO = survey;
  }

  getSurveyDTO(): ISurveyDTO | null {
    return this.surveyDTO;
  }

  saveSurveyContactGuestAndRedirect(surveyContactGuestDto: ISurveyContactGuestDto) {
    this.saveSurveyContactGuest$(surveyContactGuestDto)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.redirectAfterSave());
  }

  saveSurveyAndRedirect(visitorId: string) {
    this.saveSurveyAndRedirect$(visitorId).pipe(takeUntil(this.onDestroy$)).subscribe();
  }

  saveSurveyAndRedirect$(visitorId: string): Observable<void> {
    const survey = this.survey;
    const draft = this.surveyDTO;

    if (!survey || !draft) {
      return throwError(() => new Error(SURVEY_NOT_LOADED_MESSAGE));
    }

    this.setVenueDataInLocalStorage(survey);
    this.surveyDTO = { ...draft, visitorId };

    return this.saveSurvey$({ ...draft, visitorId }).pipe(
      tap(response => {
        this.askPermissionToContact = response.askPermissionToContact;
        this.applyVisitorSurveyData(survey, response);

        if (this.askOneMoreQuestion) {
          void this.router.navigate([
            `${ONE_MORE_QUESTION_CONSTANTS.ROUTES.MAIN}/${survey.statementSet.venueUuid}`,
          ]);
        } else {
          this.navigateToPermissionToContact();
        }
      }),
      map((): void => undefined)
    );
  }

  /**
   * The visitor-derived config, which all arrives with the submit response: which
   * demographics to ask for, whether the wheel age gate allows a spin, and the drawn extra
   * question (null when the venue has none eligible for this guest).
   */
  private applyVisitorSurveyData(
    survey: ISurvey,
    response: ApiTypes.SubmitTakeawaySurveyResponseDto
  ): void {
    this.visitorFlags = {
      shouldAskForGender: response.shouldAskForGender,
      shouldAskForAgeGroup: response.shouldAskForAgeGroup,
    };

    if (!response.canSpinWheel) {
      survey.wheel = SUPPRESSED_WHEEL;
    }

    if (response.oneMoreQuestion) {
      this.askOneMoreQuestion = true;
      this.oneMoreQuestionService.setOneMoreQuestionResponse(
        toOneMoreQuestion(response.oneMoreQuestion)
      );
    }

    this.demographicsService.setDemographicsConfig({
      ...survey.demographics,
      ...this.visitorFlags,
    });
    this.demographicsService.setGenderOptionsSource({
      venueUuid: survey.statementSet.venueUuid,
    });
    this.wheelOfFortuneService.setWheelConfig(survey.wheel);
    this.wheelOfFortuneService.setWheelOfFortuneEnable(survey.wheel.isActive);
    this.wheelOfFortuneService.prizeText = survey.wheel.prizeText;
  }

  navigateToPermissionToContact(): void {
    if (this.askPermissionToContact) {
      void this.router.navigate([
        `${CONTACT_GUEST_CONSTANTS.ROUTES.MAIN}/${CONTACT_GUEST_CONSTANTS.ROUTES.STATUSES.ASK}`,
      ]);
    } else {
      this.goToNextSteps();
    }
  }

  redirectAfterSave() {
    this.askPermissionToContact = false;
    this.askOneMoreQuestion = false;

    if (this.survey?.advertisement?.fileUuid) {
      this.getAndSetAdvertisement();
    } else {
      this.finishTheSurvey();
    }
  }

  sendFingerprintDataAnalytics(
    token: string,
    data: ExtendedGetResult,
    isBlocked: boolean,
    checkInUuid?: string | null
  ) {
    const fingerprintDTO: ApiTypes.StoreFingerprintCommand = {
      token,
      data: { ...data },
      isBlocked,
      checkInUuid,
    };

    return this.api.post(Api.surveys.takeaway.fingerprint, { body: fingerprintDTO });
  }

  saveDemographics(demographicsDto: IDemographicsDto) {
    this.saveSurveyDemographics$(demographicsDto)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.goToHappyWheelOrAdvertisement());
  }

  goToHappyWheelAfterDemographicsCanceling(demographicsDto: IDemographicsDto) {
    this.saveSurveyDemographics$(demographicsDto).pipe(takeUntil(this.onDestroy$)).subscribe();

    if (this.survey?.wheel.isDemographicsEnabled) {
      void this.router.navigate([
        `${CONSTANTS_ROUTING.ROUTES.ADD.MAIN}/${CONSTANTS_ROUTING.ROUTES.ADD.THANK_YOU}`,
      ]);
    } else {
      this.goToHappyWheelOrAdvertisement();
    }
  }

  goToHappyWheelOrAdvertisement() {
    if (this.survey?.wheel.isActive) {
      this.wheelOfFortuneService.setWheelOfFortuneBlocked(false);

      if (this.wheelOfFortuneService.isAgeGroupAllowedForWheelSpin()) {
        void this.router.navigate([WHEEL_OF_FORTUNE_ROUTING_CONSTANTS.ROUTES.HAPPY_WHEEL.MAIN]);
      } else {
        void this.router.navigate([
          `${CONSTANTS_ROUTING.ROUTES.ADD.MAIN}/${CONSTANTS_ROUTING.ROUTES.ADD.THANK_YOU}`,
        ]);
      }
    } else {
      void this.router.navigate([
        `${CONSTANTS_ROUTING.ROUTES.ADD.MAIN}/${CONSTANTS_ROUTING.ROUTES.ADD.THANK_YOU}`,
      ]);
    }
  }

  checkLocalStorage(id: string): boolean {
    if (this.hasNoVenueFraudDetection(id)) {
      return true;
    }
    const key = `${CONSTANTS.LOCALSTORAGE_KEYS.SUR}${id}`;
    const stored = readJson(key);
    const data = isStoredVenueVisit(stored) ? stored : null;

    if (data) {
      if (data.uuid === id) {
        const expiresDate: Date = new Date(data.dateTime);
        const actualDate: Date = new Date();

        return actualDate.getTime() > expiresDate.getTime();
      }

      return true;
    }

    return true;
  }

  hasNoVenueFraudDetection(venueUuid: string): boolean {
    return !!venues_without_fraud_detection.find(uuid => uuid === venueUuid);
  }

  private goToNextSteps() {
    if (this.survey?.advertisement?.fileUuid) {
      this.getAndSetAdvertisement();
    } else {
      this.redirectAfterSave();
    }
  }

  private blockHappyWheel() {
    this.wheelOfFortuneService.setWheelOfFortuneBlocked(true);

    if (this.survey?.advertisement?.fileUuid) {
      this.getAndSetAdvertisement();
    } else {
      this.finishTheSurvey();
    }
  }

  private saveSurveyDemographics$(demographicsDto: IDemographicsDto): Observable<unknown> {
    if (!this.surveyId) {
      return throwError(() => new Error(SURVEY_NOT_LOADED_MESSAGE));
    }

    return this.api
      .put(Api.surveys.takeaway.demographics, {
        body: { ...demographicsDto, token: this.surveyId },
      })
      .pipe(
        catchError((error: unknown) => {
          this.setSurveyState(SurveyStates.error);

          return throwError(() => error);
        })
      );
  }

  private finishTheSurvey() {
    this.setSurveyState(SurveyStates.complete);

    if (
      this.survey?.demographics.isEnabled &&
      (this.visitorFlags.shouldAskForAgeGroup || this.visitorFlags.shouldAskForGender)
    ) {
      this.goToDemographicsForm();
    } else {
      this.goToHappyWheelOrAdvertisement();
    }
  }

  private goToDemographicsForm() {
    if (this.survey?.wheel.isDemographicsEnabled && this.survey.wheel.isActive) {
      void this.router.navigate([
        `${DEMOGRAPHICS_CONSTANTS.ROUTES.MAIN}/${DEMOGRAPHICS_CONSTANTS.ROUTES.FORMS.WHEEL_OF_FORTUNE}`,
      ]);
    } else {
      void this.router.navigate([DEMOGRAPHICS_CONSTANTS.ROUTES.MAIN]);
    }
  }

  private getAndSetAdvertisement(): void {
    this.finishTheSurvey();
  }

  private setVenueDataInLocalStorage(survey: ISurvey) {
    const { venueUuid } = survey.statementSet;

    if (this.hasNoVenueFraudDetection(venueUuid)) {
      return;
    }
    const fourHour = 4;
    const key = `${CONSTANTS.LOCALSTORAGE_KEYS.SUR}${venueUuid}`;
    const data: StoredVenueVisit = {
      uuid: venueUuid,
      dateTime: this.utilsService.getNewDateAfterHours(fourHour),
    };

    localStorage.setItem(key, JSON.stringify(data));
  }

  getAdvertisement(): Observable<IMedia | null> {
    if (this.campaignSurvey) {
      const { venueUuid, visitorId } = this.campaignSurvey;
      return this.fetchCampaignAd$(venueUuid, visitorId).pipe(
        takeUntil(this.onDestroy$),
        map((ad: IAdvertisement) => this.advertisementService.getMediaFromAdvertisement(ad)),
        share()
      );
    }

    if (!this.surveyDTO?.visitorId) {
      return of(null);
    }
    const { token, visitorId } = this.surveyDTO;
    return this.fetchAd$(token, visitorId).pipe(
      takeUntil(this.onDestroy$),
      map((ad: IAdvertisement) => this.advertisementService.getMediaFromAdvertisement(ad)),
      share()
    );
  }

  getAdvertisementTrackingContext(): { venueUuid?: string; fingerprint?: string } {
    if (this.campaignSurvey) {
      return {
        venueUuid: this.campaignSurvey.venueUuid,
        fingerprint: this.campaignSurvey.visitorId,
      };
    }

    return {
      venueUuid: this.survey?.statementSet?.venueUuid,
      fingerprint: this.surveyDTO?.visitorId,
    };
  }

  /**
   * Identifies the device and registers it against this survey token, resolving with
   * the visitorId that was stored. Chain the submit onto this: the fill is paired to
   * that row, and a fill stored before the row exists is archived as fraud.
   *
   * Runs at submit rather than page load so a guest who only looks at the survey
   * costs no identification. The POST's failure is swallowed — the guest's response
   * matters more than the analytics row — and identification$ always yields an id,
   * falling back to a cached or per-tab one, so the submit can never be blocked.
   */
  registerFingerprint$(): Observable<string> {
    const venueUuid = this.survey?.statementSet.venueUuid;
    const surveyId = this.surveyId;

    if (!venueUuid || !surveyId) {
      return throwError(() => new Error(SURVEY_NOT_LOADED_MESSAGE));
    }

    const isBlocked = !this.checkLocalStorage(venueUuid);

    // Write-only today; kept alongside the value it describes.
    localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.HP_BLK, isBlocked ? 'true' : 'false');

    return this.fingerprintService.identification$().pipe(
      take(1),
      switchMap(fingerprint =>
        this.sendFingerprintDataAnalytics(
          surveyId,
          fingerprint,
          isBlocked,
          this.getCheckInId(venueUuid)
        ).pipe(
          catchError(() => of(null)),
          map(() => fingerprint.visitorId)
        )
      )
    );
  }

  private getCheckInId(venueUuid: string): string | null {
    const checkIn = readJson(`${CONSTANTS.LOCALSTORAGE_KEYS.CHECKED_VEN}${venueUuid}`);

    return typeof checkIn === 'object' &&
      checkIn !== null &&
      'checkInUuid' in checkIn &&
      typeof checkIn.checkInUuid === 'string'
      ? checkIn.checkInUuid
      : null;
  }

  setCampaignSurvey(venueUuid: string, visitorId: string) {
    this.campaignSurvey = {
      venueUuid,
      visitorId,
    };
  }

  clearCampaignSurvey(): void {
    this.campaignSurvey = null;
  }
}
